<template>
  <div>
    <div class="d-flex flex-wrap align-items-center mb-3">
      <button
        v-for="category in categories"
        :key="category.key"
        type="button"
        class="btn btn-sm rounded-pill d-inline-flex align-items-center font-size-sm py-1 px-2 mr-2 mb-2 font-w400"
        :class="selectedCategory === category.key ? 'btn-primary' : 'btn-outline-secondary'"
        @click="selectCategory(category.key)"
      >
        <i
          class="fa mr-2"
          :class="category.icon"
        />
        <span>{{ category.label }}</span>
      </button>
    </div>

    <div
      v-if="loading && !loaded"
      class="text-center py-4"
    >
      <b-spinner
        small
        label="Loading activity"
      />
    </div>

    <div
      v-else-if="error"
      class="text-muted mb-3"
    >
      Unable to load activities.
      <b-link
        class="alert-link ml-1"
        @click.prevent="fetchActivities"
      >
        Retry
      </b-link>
    </div>

    <div
      v-else-if="loaded && !activities.length"
      class="text-muted mb-3"
    >
      There are no activities yet.
    </div>
    <template v-else-if="loaded">
      <div
        v-for="group in groups"
        :key="group.key"
        class="mb-4"
      >
        <div class="d-flex align-items-center mb-3">
          <span class="activity-date-badge badge badge-pill text-primary font-w600 font-size-sm text-nowrap">
            {{ group.label }}
          </span>
          <div class="flex-grow-1 border-top ml-3" />
        </div>

        <div class="activity-list">
          <timeline-item
            v-for="activity in group.activities"
            :key="activity.id"
            :value="activity"
          />
        </div>
      </div>

      <div
        v-if="totalRows > perPage"
        class="d-flex justify-content-center mt-3"
      >
        <b-pagination
          v-model="page"
          :total-rows="totalRows"
          :per-page="perPage"
          last-class="d-none"
        />
      </div>
    </template>
  </div>
</template>

<script>
import { DateTime } from 'luxon';
import {
  WORK_ORDER_ACTIVITY_FILTER_ALL,
  WORK_ORDER_ACTIVITY_FILTER_CATEGORIES,
} from '@/constants/workOrderActivities';
import TimelineItem from '@/components/admin/workOrderActivities/TimelineItem.vue';

export default {
  name: 'WorkOrderActivitiesTimeline',
  components: {
    TimelineItem,
  },
  props: {
    workOrderId: {
      type: Number,
      required: true,
    },
    refreshKey: {
      type: [Number, String],
      default: 0,
    },
  },
  data() {
    return {
      loaded: false,
      loading: false,
      error: false,
      activities: [],
      totalRows: 0,
      page: 1,
      perPage: 20,
      selectedCategory: WORK_ORDER_ACTIVITY_FILTER_ALL,
      categories: WORK_ORDER_ACTIVITY_FILTER_CATEGORIES,
    };
  },
  computed: {
    groups() {
      const byDate = {};

      this.activities.forEach((activity) => {
        const date = DateTime.fromISO(activity.occurred_at);
        const key = date.toISODate();
        if (!byDate[key]) {
          byDate[key] = { key, label: this.dateLabel(date), activities: [] };
        }
        byDate[key].activities.push(activity);
      });

      return Object.values(byDate);
    },
  },
  watch: {
    page: 'fetchActivities',
    // Resetting page to 1 triggers the page watcher; only fetch here if already on page 1.
    selectedCategory() {
      if (this.page === 1) {
        this.fetchActivities();
      } else {
        this.page = 1;
      }
    },
    refreshKey() {
      if (this.page === 1) {
        this.fetchActivities();
      } else {
        this.page = 1;
      }
    },
  },
  mounted() {
    this.fetchActivities();
  },
  methods: {
    dateLabel(date) {
      const today = DateTime.local().startOf('day');
      const day = date.startOf('day');
      const daysAgo = Math.round(today.diff(day, 'days').days);

      if (daysAgo === 0) {
        return 'Today';
      }
      if (daysAgo === 1) {
        return 'Yesterday';
      }
      return day.toFormat(day.year === today.year ? 'cccc, LLL d' : 'LLL d, yyyy');
    },
    selectCategory(category) {
      this.selectedCategory = category;
    },
    fetchActivities() {
      this.loading = true;
      this.error = false;

      this.$workOrdersAPI
        .getActivities(this.workOrderId, {
          page: this.page,
          per_page: this.perPage,
          category: this.selectedCategory === WORK_ORDER_ACTIVITY_FILTER_ALL ? undefined : this.selectedCategory,
        })
        .then((response) => {
          this.activities = response.activities || [];
          this.totalRows = response.total || 0;
          // Tab badge should stay the unfiltered total.
          if (this.selectedCategory === WORK_ORDER_ACTIVITY_FILTER_ALL) {
            this.$emit('total-rows-changed', this.totalRows);
          }
        })
        .catch(() => {
          this.activities = [];
          this.totalRows = 0;
          this.error = true;
          if (this.selectedCategory === WORK_ORDER_ACTIVITY_FILTER_ALL) {
            this.$emit('total-rows-changed', 0);
          }
        })
        .finally(() => {
          this.loaded = true;
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.activity-date-badge {
  background-color: rgba(13, 110, 253, 0.08);
  padding: 0.4rem 0.75rem;
}

.activity-list {
  position: relative;
}

.activity-list::before {
  content: '';
  position: absolute;
  top: 0.75rem;
  bottom: 1.5rem;
  left: 0.95rem;
  width: 2px;
  background: #e9ecef;
}
</style>
